import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DetailFailure, DetailHeader, DetailSkeleton, OUTLINE_LINK } from "../../components/nocs/detail-parts";
import { MedalCounts } from "../../components/nocs/medal-counts";
import { NocEntries } from "../../components/nocs/noc-entries";
import { Flag } from "../../components/nocs/noc-badge";
import { NocMedals } from "../../components/nocs/noc-medals";
import { NocSportsTable } from "../../components/nocs/noc-sports-table";
import { NocTabs } from "../../components/nocs/noc-tabs";
import { nocDetailQuery } from "../../components/nocs/queries";
import { type NocSearch, nocEntriesPaging, nocSearch, nocSearchForTab, nocTabOf } from "../../components/nocs/search";
import { useLinkedGames } from "../../components/nocs/use-linked-games";
import { ShellCrumbs } from "../../components/shell/crumbs";
import { EmptyState } from "../../components/ui/empty-state";
import { formatInteger } from "../../lib/i18n/format";
import { nocName } from "../../components/nocs/noc-name";

const isUuid = (v: string) => z.string().uuid().safeParse(v).success;

/**
 * One NOC in one games (`GET /nocs/:id` — totals and one row per sport, in
 * one read). Its entries (`GET /competitors?responsible_organization_id=`,
 * paged) and its medals (`GET /medals?responsible_organization_id=`, keyset)
 * load only when their tab is opened. The tab and the entries page are in
 * the URL (`?games&tab&page&size`).
 */
export const Route = createFileRoute("/_app/nocs/$nocId")({
	validateSearch: nocSearch,
	component: NocPage,
});

function NocPage() {
	const { t } = useTranslation("nocs");
	const { nocId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/nocs/$nocId" });
	const go = (next: NocSearch, replace = false) => void navigate({ search: next, replace });
	const games = useLinkedGames(search.games, (id) => go({ ...search, games: id, page: undefined }, true));
	const valid = isUuid(nocId);
	const id = nocId.toLowerCase();
	const panelId = useId();

	const q = useQuery({ ...nocDetailQuery(games.gamesId ?? "", id), enabled: games.gamesId !== null && valid });
	const noc = q.data;
	const gamesName = games.games?.name ?? "";
	const tab = nocTabOf(search);

	const crumbs = <ShellCrumbs items={[{ label: t("title"), to: { to: "/nocs" } }, { label: noc ? (noc.code ?? nocName(noc)) : t("detailTitle") }]} />;

	if (!valid || q.isError) {
		return (
			<div className="flex flex-col gap-[1rem]">
				{crumbs}
				<h1 className="font-black text-[1.5rem] text-ink">{t("detailTitle")}</h1>
				<DetailFailure
					error={valid ? q.error : "not-found"}
					notFound={{
						title: t("detail.notFoundTitle"),
						body: t("detail.notFoundBody"),
						action: (
							<Link to="/nocs" className={OUTLINE_LINK}>
								{t("detail.backToList")}
							</Link>
						),
					}}
					onRetry={() => void q.refetch()}
				/>
			</div>
		);
	}

	if (!noc || games.gamesId === null) {
		return (
			<div className="flex flex-col">
				{crumbs}
				<DetailSkeleton label={t("detail.loading")} />
			</div>
		);
	}

	const gamesId = games.gamesId;
	const label = noc.code ?? nocName(noc);
	const { page, pageSize } = nocEntriesPaging(search);

	return (
		<div className="flex flex-col">
			{crumbs}
			<DetailHeader
				kicker={
					<span className="flex items-center gap-[0.75rem]">
						<Flag url={noc.flag_url} code={noc.code} size="lg" />
						{noc.code && <span className="font-black font-mono text-[1.5rem] text-board tracking-[0.04em]">{noc.code}</span>}
					</span>
				}
				title={nocName(noc)}
				facts={[
					{ key: "athletes", label: t("detail.facts.athletes"), value: <span className="font-mono">{formatInteger(noc.athletes_count)}</span> },
					{ key: "entries", label: t("detail.facts.entries"), value: <span className="font-mono">{formatInteger(noc.entries_count)}</span> },
					{ key: "sports", label: t("detail.facts.sports"), value: <span className="font-mono">{formatInteger(noc.sports.length)}</span> },
				]}
				actions={
					noc.athletes_count > 0 ? (
						<Link to="/athletes" search={{ noc: noc.responsible_organization_id }} className={OUTLINE_LINK}>
							{t("detail.athletesLink")}
						</Link>
					) : undefined
				}
				aside={<MedalCounts medals={noc.medals} size="lg" />}
			/>

			{noc.athletes_count === 0 && noc.entries_count === 0 ? (
				<EmptyState title={t("detail.noAthletesTitle", { noc: label, games: gamesName })} body={t("detail.noEntriesBody")} />
			) : (
				<>
					<NocTabs value={tab} onChange={(next) => go(nocSearchForTab(search, next))} panelId={panelId} />
					<div id={panelId} role="tabpanel" aria-labelledby={`${panelId}-${tab}`}>
						{tab === "sports" && (
							<NocSportsTable
								sports={noc.sports}
								onOpen={(s) => void navigate({ to: "/teams", search: { noc: noc.responsible_organization_id, sport: s.sport.sport_id } })}
							/>
						)}
						{tab === "entries" && (
							<NocEntries
								gamesId={gamesId}
								nocId={noc.responsible_organization_id}
								page={page}
								pageSize={pageSize}
								empty={{ title: t("detail.noEntriesTitle", { noc: label, games: gamesName }), description: t("detail.noEntriesBody") }}
								onPage={(next, replace) => go({ games: search.games, tab: "entries", page: next.page, size: next.size }, replace)}
							/>
						)}
						{tab === "medals" && <NocMedals gamesId={gamesId} nocId={noc.responsible_organization_id} />}
					</div>
				</>
			)}
		</div>
	);
}
